import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import EngineeringDashboard from "@/pages/EngineeringDashboard";
import OperationalDashboard from "@/pages/OperationalDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import ServiceOrdersPage from "@/pages/ServiceOrdersPage";
import CronogramasPage from "@/pages/CronogramasPage";
import CronogramasDashboardPage from "@/pages/CronogramasDashboardPage";
import KanbanPage from "@/pages/KanbanPage";
import DailyLogsPage from "@/pages/DailyLogsPage";
import ProjectsPage from "@/pages/ProjectsPage";
import FinancialPage from "@/pages/FinancialPage";
import UsersPage from "@/pages/UsersPage";
import TrackingPage from "@/pages/TrackingPage";
import CargosPage from "@/pages/CargosPage";
import ClientesFornecedoresPage from "@/pages/ClientesFornecedoresPage";
import FuncionariosPage from "@/pages/FuncionariosPage";
// Financial Pages
import CentroCustoPage from "@/pages/CentroCustoPage";
import PlanoContasPage from "@/pages/PlanoContasPage";
import DREGeralPage from "@/pages/DREGeralPage";
import DREContratoPage from "@/pages/DREContratoPage";
import FluxoCaixaPage from "@/pages/FluxoCaixaPage";
import ContasPagarReceberPage from "@/pages/ContasPagarReceberPage";
import CartoesCreditoPage from "@/pages/CartoesCreditoPage";
import CategoriasPage from "@/pages/CategoriasPage";
import BancosPage from "@/pages/BancosPage";
import ConciliacaoPage from "@/pages/ConciliacaoPage";
import AdminCleanupPage from "@/pages/AdminCleanupPage";
// Project Pages
import OrcamentosPage from "@/pages/OrcamentosPage";
import ContratosPage from "@/pages/ContratosPage";
import PrecificacaoPage from "@/pages/PrecificacaoPage";
import CRMPage from "@/pages/CRMPage";
import CRMDashboardPage from "@/pages/CRMDashboardPage";
import MeuDiaPage from "@/pages/MeuDiaPage";
import CRMConfigPage from "@/pages/CRMConfigPage";
import AdminSecurityPage from "@/pages/AdminSecurityPage";
// Cadastros Pages
import EstoquePage from "@/pages/EstoquePage";
import PatrimonioPage from "@/pages/PatrimonioPage";
import PatrimonioOrganizarPage from "@/pages/PatrimonioOrganizarPage";
// Logística (Fase 2 — Romaneio FL-05)
import RomaneiosPage from "@/pages/logistica/RomaneiosPage";
import NovaSaidaPage from "@/pages/logistica/NovaSaidaPage";
import RomaneioDetalhePage from "@/pages/logistica/RomaneioDetalhePage";
import ConferenciaPage from "@/pages/logistica/ConferenciaPage";
import ConferenciaPublicaPage from "@/pages/logistica/ConferenciaPublicaPage";
import MovimentacoesLogisticaPage from "@/pages/logistica/MovimentacoesLogisticaPage";
import CautelasPage from "@/pages/logistica/CautelasPage";
import OcorrenciasPage from "@/pages/logistica/OcorrenciasPage";
import ConferenciaPapelPage from "@/pages/logistica/ConferenciaPapelPage";
import CadernoObraPage from "@/pages/logistica/CadernoObraPage";
import RevisaoLancamentosPage from "@/pages/logistica/RevisaoLancamentosPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDashboardPath(user.role)} replace />;
  }

  return children;
};

// Get dashboard path by role
const getDashboardPath = (role) => {
  switch (role) {
    case "admin":
      return "/admin";
    case "engineering":
      return "/engineering";
    case "operational":
      return "/operational";
    case "client":
      return "/client";
    default:
      return "/login";
  }
};

// Axios interceptor setup - returns cleanup function
let requestInterceptorId = null;
let responseInterceptorId = null;

const setupAxiosInterceptors = (token, logout) => {
  // Remove existing interceptors before adding new ones
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axios.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Não deslogar quando a request opta por tratar 401 localmente
      // (ex.: exclusão segura que pede senha — senha errada = 401 no endpoint,
      // não é expiração de sessão).
      const skip = error.config?.skipAuthInterceptor;
      if (error.response?.status === 401 && !skip) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  // Return cleanup function
  return () => {
    if (requestInterceptorId !== null) {
      axios.interceptors.request.eject(requestInterceptorId);
      requestInterceptorId = null;
    }
    if (responseInterceptorId !== null) {
      axios.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
  };
};

function App() {
  const [user, setUser] = useState(null);
  // Token de sessão: apenas sessionStorage (expira ao fechar a aba).
  // Migra e remove tokens legados persistidos em localStorage.
  const [token, setToken] = useState(() => {
    const legacy = localStorage.getItem("luvcorp_token");
    if (legacy) {
      if (!sessionStorage.getItem("luvcorp_token")) {
        sessionStorage.setItem("luvcorp_token", legacy);
      }
      localStorage.removeItem("luvcorp_token");
    }
    return sessionStorage.getItem("luvcorp_token");
  });
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    // Armazena APENAS em sessionStorage (sessão por aba, some ao fechar)
    sessionStorage.setItem("luvcorp_token", access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    sessionStorage.setItem("luvcorp_token", access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    sessionStorage.removeItem("luvcorp_token");
    localStorage.removeItem("luvcorp_token"); // limpeza de token legado
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth error:", error);
          logout();
        }
      }
      setLoading(false);
    };

    const cleanup = setupAxiosInterceptors(token, logout);
    initAuth();

    // Cleanup interceptors on unmount or token change
    return () => {
      cleanup();
    };
  }, [token]);

  const authValue = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    getDashboardPath
  };

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={authValue}>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/track/:linkId" element={<TrackingPage />} />
            <Route path="/conferencia-romaneio/:id" element={<ConferenciaPublicaPage />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Engineering Routes */}
          <Route
            path="/engineering/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <EngineeringDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Operational Routes */}
          <Route
            path="/operational/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <OperationalDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Client Routes */}
          <Route
            path="/client/*"
            element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Shared Routes */}
          <Route
            path="/service-orders/*"
            element={
              <ProtectedRoute>
                <ServiceOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operacional/cronogramas/*"
            element={
              <ProtectedRoute>
                <CronogramasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operacional/cronogramas-dashboard"
            element={
              <ProtectedRoute>
                <CronogramasDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kanban"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <KanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-logs/*"
            element={
              <ProtectedRoute>
                <DailyLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/*"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Comercial - Propostas e Contratos */}
          <Route
            path="/comercial/propostas/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <OrcamentosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/contratos/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <ContratosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/precificacao"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <PrecificacaoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/crm/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <CRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/dashboard"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <CRMDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/meu-dia"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <MeuDiaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/configuracoes"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <CRMConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seguranca"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminSecurityPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financial/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <FinancialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          
          {/* Cadastros Routes */}
          <Route
            path="/cadastros/cargos/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <CargosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/clientes-fornecedores/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <ClientesFornecedoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/funcionarios/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <FuncionariosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/usuarios/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/estoque/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <EstoquePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/ferramentas/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <EstoquePage modo="retornavel" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/patrimonio-organizar"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <PatrimonioOrganizarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cadastros/patrimonio/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <PatrimonioPage />
              </ProtectedRoute>
            }
          />

          {/* Logística (Fase 2 — Romaneio FL-05) */}
          <Route
            path="/logistica/romaneios"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <RomaneiosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/nova-saida"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <NovaSaidaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/romaneios/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <RomaneioDetalhePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/conferencia"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <ConferenciaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/conferencia-papel/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <ConferenciaPapelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/caderno"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <CadernoObraPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/revisao-antigos"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <RevisaoLancamentosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/movimentacoes"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <MovimentacoesLogisticaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/cautelas"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <CautelasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/ocorrencias"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering", "operational"]}>
                <OcorrenciasPage />
              </ProtectedRoute>
            }
          />

          
          {/* Financeiro Routes */}
          <Route
            path="/financeiro"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <FinancialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/centro-custo/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <CentroCustoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/plano-contas/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PlanoContasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/dre-geral"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DREGeralPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/dre-contrato"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <DREContratoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/fluxo-caixa"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <FluxoCaixaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/contas/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <ContasPagarReceberPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/cartoes/*"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <CartoesCreditoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/categorias"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <CategoriasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/bancos"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <BancosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/conciliacao"
            element={
              <ProtectedRoute allowedRoles={["admin", "engineering"]}>
                <ConciliacaoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cleanup"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminCleanupPage />
              </ProtectedRoute>
            }
          />
          
          {/* Default Redirect */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={getDashboardPath(user.role)} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
